import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;

const client = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  withCredentials: true, // send/receive httpOnly auth cookies
});

// Normalize FastAPI/Pydantic 422 errors so toast strings never crash React.
client.interceptors.response.use(
  (r) => r,
  (error) => {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) {
      error.response.data.detail = d.map((e) => e?.msg || String(e)).join("; ");
    } else if (d && typeof d === "object") {
      error.response.data.detail = d.msg || JSON.stringify(d);
    }

    // 401 on any protected admin endpoint → kick to /login.
    // Skip /auth/me (that endpoint is HOW we check session state).
    const url = error?.config?.url || "";
    const onLogin = window.location.pathname === "/login";
    if (error?.response?.status === 401 && !url.includes("/auth/me") && !onLogin) {
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export const api = {
  // ---- Auth ----
  login: (email, password) =>
    client.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),

  // ---- Shopify mock products (admin) ----
  listProducts: () => client.get("/products").then((r) => r.data),

  // ---- Admin campaigns ----
  listCampaigns: () => client.get("/campaigns").then((r) => r.data),
  createCampaign: (payload) => client.post("/campaigns", payload).then((r) => r.data),
  getCampaign: (id) => client.get(`/campaigns/${id}`).then((r) => r.data),
  updateCampaign: (id, payload) => client.put(`/campaigns/${id}`, payload).then((r) => r.data),
  deleteCampaign: (id) => client.delete(`/campaigns/${id}`).then((r) => r.data),
  activateCampaign: (id) => client.post(`/campaigns/${id}/activate`).then((r) => r.data),
  launchForSale: (id) => client.post(`/campaigns/${id}/launch-sale`).then((r) => r.data),
  bulkPreorder: (payload) => client.post("/campaigns/bulk-preorder", payload).then((r) => r.data),
  fulfillAll: (id) => client.post(`/campaigns/${id}/fulfill-all`).then((r) => r.data),
  listVotes: (id) => client.get(`/campaigns/${id}/votes`).then((r) => r.data),
  listPreorders: (id) => client.get(`/campaigns/${id}/preorders`).then((r) => r.data),

  // ---- Admin rounds (gallery format only) ----
  listRounds: () => client.get("/rounds?format=gallery").then((r) => r.data),
  createRound: (payload) => client.post("/rounds", { ...payload, format: "gallery" }).then((r) => r.data),
  getRound: (id) => client.get(`/rounds/${id}`).then((r) => r.data),
  updateRound: (id, payload) => client.put(`/rounds/${id}`, payload).then((r) => r.data),
  deleteRound: (id) => client.delete(`/rounds/${id}`).then((r) => r.data),
  finalizeRound: (id) => client.post(`/rounds/${id}/finalize`).then((r) => r.data),

  // ---- Admin reels (rounds with format=reel) ----
  listReels: () => client.get("/rounds?format=reel").then((r) => r.data),
  createReel: (payload) => client.post("/rounds", { ...payload, format: "reel" }).then((r) => r.data),
  getReel: (id) => client.get(`/rounds/${id}`).then((r) => r.data),
  finalizeReel: (id) => client.post(`/rounds/${id}/finalize`).then((r) => r.data),
  deleteReel: (id) => client.delete(`/rounds/${id}`).then((r) => r.data),
  publicListReels: () => client.get("/public/rounds?format=reel").then((r) => r.data),
  publicGetReel: (id) => client.get(`/public/rounds/${id}`).then((r) => r.data),

  // ---- Public rounds ----
  publicListRounds: () => client.get("/public/rounds?format=gallery").then((r) => r.data),
  publicGetRound: (id) => client.get(`/public/rounds/${id}`).then((r) => r.data),

  // ---- Shopify connection ----
  shopifyStatus: () => client.get("/shopify/status").then((r) => r.data),
  shopifyDisconnect: () => client.post("/shopify/disconnect").then((r) => r.data),

  // ---- Public storefront-facing ----
  publicListCampaigns: () => client.get("/public/campaigns").then((r) => r.data),
  publicStatus: (id) => client.get(`/campaigns/${id}/status`).then((r) => r.data),
  vote: (campaignId, email, referredBy) =>
    client.post("/vote", { campaignId, email, referredBy: referredBy || null }).then((r) => r.data),
  preorder: (campaignId, customerEmail, customerName) =>
    client.post("/preorder", { campaignId, customerEmail, customerName }).then((r) => r.data),
  leaderboard: (id) => client.get(`/campaigns/${id}/leaderboard`).then((r) => r.data),
  voterLookup: (id, email) =>
    client.get(`/campaigns/${id}/voter`, { params: { email } }).then((r) => r.data),

  // ---- Stats (admin) ----
  stats: () => client.get("/stats").then((r) => r.data),

  // ---- Demo helpers (admin) ----
  seedDemo: () => client.post("/demo/seed").then((r) => r.data),
  resetDemo: () => client.post("/demo/reset").then((r) => r.data),

  // ---- The Cut applications ----
  submitCutApplication: (payload) => client.post("/cut/applications", payload).then((r) => r.data),
  listCutApplications: (status) =>
    client.get("/cut/applications", { params: status ? { status } : {} }).then((r) => r.data),
  updateCutApplication: (id, payload) =>
    client.patch(`/cut/applications/${id}`, payload).then((r) => r.data),
  deleteCutApplication: (id) => client.delete(`/cut/applications/${id}`).then((r) => r.data),

  // ---- The Cut — brand competition ----
  cutActiveRound: () => client.get("/the-cut/rounds/active").then((r) => r.data),
  cutGetBrand: (slug) => client.get(`/the-cut/brands/${slug}`).then((r) => r.data),
  cutGetLeaderboard: (roundId) => client.get(`/the-cut/rounds/${roundId}/leaderboard`).then((r) => r.data),
  cutVote: (productId, voterToken) =>
    client.post("/the-cut/vote", { productId, voterToken }).then((r) => r.data),
  cutSeed: () => client.post("/the-cut/seed").then((r) => r.data),
  cutAttachVoterEmail: (voterToken, email) =>
    client.post("/the-cut/voter", { voterToken, email }).then((r) => r.data),

  // ---- The Cut — admin ----
  cutAdminListRounds: () => client.get("/the-cut/rounds").then((r) => r.data),
  cutAdminCreateRound: (payload) => client.post("/the-cut/rounds", payload).then((r) => r.data),
  cutAdminUpdateRound: (id, payload) => client.patch(`/the-cut/rounds/${id}`, payload).then((r) => r.data),
  cutAdminDeleteRound: (id) => client.delete(`/the-cut/rounds/${id}`).then((r) => r.data),
  cutAdminCloseRound: (id) => client.post(`/the-cut/rounds/${id}/close`).then((r) => r.data),
  cutAdminSeedVotes: (id) => client.post(`/the-cut/rounds/${id}/seed-votes`).then((r) => r.data),
  cutAdminClearVotes: (id) => client.post(`/the-cut/rounds/${id}/clear-seed-votes`).then((r) => r.data),
  cutAdminListBrands: (roundId) => client.get(`/the-cut/rounds/${roundId}/brands`).then((r) => r.data),
  cutAdminCreateBrand: (roundId, payload) => client.post(`/the-cut/rounds/${roundId}/brands`, payload).then((r) => r.data),
  cutAdminUpdateBrand: (id, payload) => client.patch(`/the-cut/brands/${id}`, payload).then((r) => r.data),
  cutAdminDeleteBrand: (id) => client.delete(`/the-cut/brands/${id}`).then((r) => r.data),
  cutAdminListProducts: (brandId) => client.get(`/the-cut/brands/${brandId}/products`).then((r) => r.data),
  cutAdminCreateProduct: (brandId, payload) => client.post(`/the-cut/brands/${brandId}/products`, payload).then((r) => r.data),
  cutAdminUpdateProduct: (id, payload) => client.patch(`/the-cut/products/${id}`, payload).then((r) => r.data),
  cutAdminDeleteProduct: (id) => client.delete(`/the-cut/products/${id}`).then((r) => r.data),
  cutAdminReorderProducts: (brandId, productIds) => client.post(`/the-cut/brands/${brandId}/products/reorder`, { productIds }).then((r) => r.data),
  cutAdminAcceptApplication: (id) => client.post(`/cut/applications/${id}/accept`).then((r) => r.data),
  cutAdminRoundAnalytics: (roundId) => client.get(`/the-cut/rounds/${roundId}/analytics`).then((r) => r.data),
  cutWinnersWall: (voterToken) => client.get("/the-cut/winners", { params: voterToken ? { voterToken } : {} }).then((r) => r.data),
  cutAdminRankTimeline: (roundId) => client.get(`/the-cut/rounds/${roundId}/rank-timeline`).then((r) => r.data),
  cutVoterJourney: (voterToken) => client.get(`/the-cut/voter/${voterToken}/journey`).then((r) => r.data),
  cutPressKit: () => client.get("/the-cut/press").then((r) => r.data),
};
